import { Injectable } from "@angular/core";
import { CurrentCaseService } from '@modules/common/services/current-case.service';
import { CurrentEnvironmentService } from '@modules/common/services/current-environment.service';

/**
 * Service to build a complete URL by automatically prepending the environment and case.
 */
@Injectable({
    providedIn: 'root'
})
export class CompleteUrlBuilderService {
	constructor(
		private _currentEnvironmentService: CurrentEnvironmentService,
		private _currentCaseService: CurrentCaseService
	) { }

	/**
	 * Builds a complete URL using the given path components.
	 * @param pathComponents The components of the path to the desired URL. A component
	 * is permitted to contain forward slashes.
	 * @param caseName If provided, this case is used, otherwise the current case is used.
	 * @param environmentName If provided, this environment is used, otherwise the current environment is used.
	 */
	public buildUrl(pathComponents?: (string | undefined)[], caseName?: string, environmentName?: string): string {
		let urlComponents: string[] = [];

		//If an environment was given, use it. Otherwise, get the current environment.
		const environmentNameToUse = environmentName || this._currentEnvironmentService.getCurrentEnvironmentName();
		//Add the environment name to the URL.
		if (environmentNameToUse) {
			urlComponents.push(environmentNameToUse);
		}

		//If a case was given, use it. Otherwise, get the current case.
		const caseNameToUse = caseName || this._currentCaseService.getCurrentCaseName();
		//Add the case name to the URL.
		if (caseNameToUse) {
			urlComponents.push(caseNameToUse);
		}

		//Add all of the passed-in components to the URL.
		if (pathComponents && pathComponents.length) {
			for (let dirtyComponent of pathComponents.filter(pathComponent => !!pathComponent)) { //Ignore undefined or empty components
				dirtyComponent!
					.split('/') //We are allowing a component to contain slashes, so split it up into the actual components
					.filter(s => !!s) //Ignore any empty resulting split elements (can happen if there are leading, trailing, or consecutive slashes)
					.forEach(cleanComponent => urlComponents.push(cleanComponent)); //Add the clean components to the list
			}
		}

		//Return the URL components joined with slashes. This is an absolute URL, so also lead with a slash.
		return '/' + urlComponents.join('/');
	}
}
